import * as React from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import CharacterCount from '@tiptap/extension-character-count';
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  List,
  ListOrdered,
  Link as LinkIcon,
  RemoveFormatting,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface RichTextEditorProps {
  /** HTML string content */
  content: string;
  /** Called with updated HTML on every change */
  onChange: (html: string) => void;
  /** Maximum number of characters (uses Tiptap CharacterCount extension) */
  maxLength?: number;
  /** Placeholder text shown when the editor is empty */
  placeholder?: string;
  /** Additional class names for the outer wrapper */
  className?: string;
  /** When true the editor is read-only */
  disabled?: boolean;
}

// ---------------------------------------------------------------------------
// Toolbar button
// ---------------------------------------------------------------------------

interface ToolbarButtonProps {
  onClick: () => void;
  isActive?: boolean;
  disabled?: boolean;
  title: string;
  children: React.ReactNode;
}

const ToolbarButton = ({ onClick, isActive, disabled, title, children }: ToolbarButtonProps) => (
  <Button
    type="button"
    variant="ghost"
    size="sm"
    title={title}
    disabled={disabled}
    onClick={onClick}
    className={cn(
      'h-7 w-7 p-0 rounded',
      isActive
        ? 'bg-primary/10 text-primary hover:bg-primary/15'
        : 'text-muted-foreground hover:text-foreground hover:bg-accent',
    )}
  >
    {children}
  </Button>
);

// ---------------------------------------------------------------------------
// RichTextEditor
// ---------------------------------------------------------------------------

const RichTextEditor = React.forwardRef<HTMLDivElement, RichTextEditorProps>(
  ({ content, onChange, maxLength, placeholder, className, disabled }, ref) => {
    const editor = useEditor({
      extensions: [
        StarterKit.configure({
          // Keep heading, bold, italic, bulletList, orderedList, etc. from starter kit
          // Disable hardBreak so Enter creates a new paragraph (cleaner HTML)
          hardBreak: false,
        }),
        Underline,
        Link.configure({
          openOnClick: false,
          autolink: true,
          HTMLAttributes: {
            class: 'text-info-500 underline cursor-pointer',
          },
        }),
        ...(maxLength !== undefined
          ? [CharacterCount.configure({ limit: maxLength })]
          : [CharacterCount]),
      ],
      content,
      editable: !disabled,
      onUpdate({ editor }) {
        // Return empty string for a blank doc to avoid orphan <p></p> in storage
        const html = editor.isEmpty ? '' : editor.getHTML();
        onChange(html);
      },
      editorProps: {
        attributes: {
          class: cn(
            'prose prose-sm dark:prose-invert max-w-none',
            'min-h-[80px] px-3 py-2 text-sm outline-none',
            'focus:outline-none',
          ),
          ...(placeholder ? { 'data-placeholder': placeholder } : {}),
        },
      },
    });

    // Sync external content changes (e.g. when parent resets the field)
    React.useEffect(() => {
      if (!editor) return;
      const current = editor.isEmpty ? '' : editor.getHTML();
      if (content !== current) {
        editor.commands.setContent(content ?? '', false);
      }
    }, [content, editor]);

    // Sync disabled prop
    React.useEffect(() => {
      if (!editor) return;
      editor.setEditable(!disabled);
    }, [disabled, editor]);

    // --- Link helper ---
    const handleSetLink = React.useCallback(() => {
      if (!editor) return;
      const previousUrl = editor.getAttributes('link').href as string | undefined;
      const url = window.prompt('Enter URL', previousUrl ?? 'https://');
      if (url === null) return; // cancelled
      if (url === '') {
        editor.chain().focus().extendMarkRange('link').unsetLink().run();
        return;
      }
      editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    }, [editor]);

    // Character count values
    const charCount = editor?.storage.characterCount?.characters?.() ?? 0;
    const isNearLimit = maxLength !== undefined && charCount >= maxLength * 0.9;
    const isAtLimit = maxLength !== undefined && charCount >= maxLength;

    return (
      <div
        ref={ref}
        className={cn(
          'rounded-md border border-input bg-background shadow-xs',
          'focus-within:border-ring focus-within:ring-ring focus-within:ring-[3px]',
          disabled && 'pointer-events-none opacity-50',
          className,
        )}
      >
        {/* ── Toolbar ──────────────────────────────────────────── */}
        <div className="flex flex-wrap items-center gap-0.5 border-b border-border px-2 py-1 bg-accent/30 rounded-t-md">
          <ToolbarButton
            title="Bold (Ctrl+B)"
            onClick={() => editor?.chain().focus().toggleBold().run()}
            isActive={editor?.isActive('bold')}
            disabled={!editor}
          >
            <Bold className="size-3.5" />
          </ToolbarButton>

          <ToolbarButton
            title="Italic (Ctrl+I)"
            onClick={() => editor?.chain().focus().toggleItalic().run()}
            isActive={editor?.isActive('italic')}
            disabled={!editor}
          >
            <Italic className="size-3.5" />
          </ToolbarButton>

          <ToolbarButton
            title="Underline (Ctrl+U)"
            onClick={() => editor?.chain().focus().toggleUnderline().run()}
            isActive={editor?.isActive('underline')}
            disabled={!editor}
          >
            <UnderlineIcon className="size-3.5" />
          </ToolbarButton>

          <div className="mx-1 h-4 w-px bg-border" />

          <ToolbarButton
            title="Bullet List"
            onClick={() => editor?.chain().focus().toggleBulletList().run()}
            isActive={editor?.isActive('bulletList')}
            disabled={!editor}
          >
            <List className="size-3.5" />
          </ToolbarButton>

          <ToolbarButton
            title="Numbered List"
            onClick={() => editor?.chain().focus().toggleOrderedList().run()}
            isActive={editor?.isActive('orderedList')}
            disabled={!editor}
          >
            <ListOrdered className="size-3.5" />
          </ToolbarButton>

          <div className="mx-1 h-4 w-px bg-border" />

          <ToolbarButton
            title="Insert / Edit Link"
            onClick={handleSetLink}
            isActive={editor?.isActive('link')}
            disabled={!editor}
          >
            <LinkIcon className="size-3.5" />
          </ToolbarButton>

          <ToolbarButton
            title="Clear Formatting"
            onClick={() =>
              editor?.chain().focus().clearNodes().unsetAllMarks().run()
            }
            disabled={!editor}
          >
            <RemoveFormatting className="size-3.5" />
          </ToolbarButton>
        </div>

        {/* ── Editor area ──────────────────────────────────────── */}
        <EditorContent editor={editor} />

        {/* ── Character count ──────────────────────────────────── */}
        {maxLength !== undefined && (
          <div className="flex justify-end border-t border-border px-3 py-1">
            <span
              className={cn(
                'text-xs tabular-nums',
                isAtLimit
                  ? 'text-destructive'
                  : isNearLimit
                    ? 'text-warning-600 dark:text-warning-500'
                    : 'text-muted-foreground',
              )}
            >
              {charCount}/{maxLength}
            </span>
          </div>
        )}
      </div>
    );
  },
);

RichTextEditor.displayName = 'RichTextEditor';

export { RichTextEditor };
